import { ref } from "vue";
import { storeToRefs } from "pinia";

import { useArticlesStore } from "@/stores/Articles";
import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import { useCategoryFilter } from "@/composables/filters/category-filter";
import { useDateRangeFilter } from "@/composables/filters/date-range-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";
import { useContentFilter } from "@/composables/filters/use-content-filter";

import { Article } from "@tmi/types/entities";
import { articleKey } from "@/types/products/product-definitions";

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/
export function useArticleFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const articlesStore = useArticlesStore();
    const { allArticles } = storeToRefs( articlesStore );
    const userStore = useUserStore();

    // -------------------------------- FILTER STATE --------------------------------- //
    const filterOutNonFavorites = ref( false );

    // ------------------------------------ FILTERS ------------------------------------
    // The categories that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedCategories } = useCategoryFilter();

    // The time interval that captures the date range for which to return articles
    const { isValidDateWithinDateRange } = useDateRangeFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutSubscriptionBasedProducts, filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    const {
        filtered: filteredArticles,
        filteredPage: filteredArticlesPage,
        hasAnyResults: hasAnyArticleResults,
        searchResultsLabel: articlesSearchResultsLabel,
    } = useContentFilter< Article >( {
        itemNoun: 'article',
        resetPageOnRecompute: true,
        computeFiltered: () => {
            // Favorites filter is applied first regardless of auth state.
            const composedArticles: Article[] = allArticles.value.filter(
                ( article: Article ) => !filterOutNonFavorites.value || userStore.isFavorited( article.contentfulId, articleKey )
            );

            if ( !isAuthenticated.value ) {
                // Unauth path: strip paywalled products, then date + text.
                return composedArticles
                    .filter( ( article: Article ) => filterOutSubscriptionBasedProducts( article.products ) )
                    .filter( ( article: Article ) => isValidDateWithinDateRange( article.publishedDate ) )
                    .filter( ( article: Article ) => filterOutItemsThatDoNotContainSearchText( [ article.text, article.preview, article.title ] ) );
            }

            // Auth path: category + product + date + text.
            return composedArticles
                .filter( ( article: Article ) => filterOutItemsThatDoNotContainSelectedCategories( article.categories ) )
                .filter( ( article: Article ) => filterOutItemsThatDoNotContainSelectedProducts( article.products ) )
                .filter( ( article: Article ) => isValidDateWithinDateRange( article.publishedDate ) )
                .filter( ( article: Article ) => filterOutItemsThatDoNotContainSearchText( [ article.text, article.preview, article.title ] ) );
        },
    } );

    return {
        // T/F based on if filteredArticles has any items
        hasAnyArticleResults,
        // '#### results found'
        articlesSearchResultsLabel,

        // The current single page sub-collection
        filteredArticlesPage,
        // All articles that match the current filter
        filteredArticles,
        // A flag that defines if only the user's favorite articles that match the given criteria
        // are returned or all that articles that match the criteria
        filterOutNonFavorites
    }
}

export type ArticleFilters = ReturnType< typeof useArticleFilters >;
